import React, { useEffect, useState } from "react";
import { SaveBtn, TopHeading, Wrapper } from "../../../common/index.style";
import { CustomContainer } from "./index.style";
import {
  Col,
  Form,
  Input,
  InputNumber,
  Radio,
  Checkbox,
  Row,
  ColorPicker,
  Select,
  Button,
  Modal,
  Card,
  Tag,
  message,
} from "antd";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  convertColorObjectToHex,
  copyToClipboard,
  hsvToHexWithAlpha,
  textSizeTags,
} from "../../../utils/helper";
import { FiCopy } from "react-icons/fi";
import {
  editPublisher,
  getPublisher,
  resetPublisher,
  syncBeehiiv,
  runBeehiivDiagnostics,
  clearDiagnostics,
} from "../../../redux/publisherSlice";
import { AiOutlinePercentage } from "react-icons/ai";
import { TbCurrencyDollar, TbFold } from "react-icons/tb";
import lookup from "country-code-lookup";
import { useMemo } from "react";
import { getSupportedCurrencies } from "../../../redux/currencySlice";
import { TEXTS } from "../../../utils/text";
import { CMS } from "../../../services/constants";

const EditPublishers = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const params = useParams();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const publisherId = params?.id;

  const store = useSelector((store) => store);
  const { loading: isSupportedCurrenciesLoading, supportedCurrencies } =
    useSelector((store) => store.currency);

  useEffect(() => {
    dispatch(getSupportedCurrencies());
  }, []);

  useEffect(() => {
    dispatch(getPublisher({ publisherId }));

    return () => {
      dispatch(resetPublisher());
    };
  }, [publisherId, dispatch]);

  const [activeCMS, setActiveCMS] = useState('Ghost'); // Default to Beehive
  const handleCmsChange = (e) => {
    if (e.target?.value) {
      setActiveCMS(e.target?.value);
      // Switching CMS is a re-setup — Ghost defaults to the alternative
      // button position (next to Subscribe), other platforms default off.
      form.setFieldsValue({ isButtonNext: e.target.value === CMS.Ghost });
    }
  };

  const { publisher, loading, diagnosticsResults } = store?.publisher || {};

  useEffect(() => {
    if (publisher) {
      form.setFieldsValue({
        publisherName: publisher?.name,
        firstName: publisher?.user?.firstName,
        lastName: publisher?.user?.lastName,
        website: publisher?.website,
        email: publisher?.email,
        country: publisher?.country,
        articlePrice: publisher?.articlePrice,
        abTest: publisher?.abTest ? "Yes" : "No",
        active: publisher?.active ? "Yes" : "No",
        apiKeyName: publisher?.apiKey ? "Yes" : "No",
        checkoutemail: publisher?.checkoutemail ? "Yes" : "No",
        revShare: publisher?.revShare1?.percentage,
        miniPrice: publisher?.revShare1?.minPrice,
        revShare2: publisher?.revShare2?.percentage,
        lowPrice2: publisher?.revShare2?.minPrice,
        highPrice2: publisher?.revShare2?.maxPrice,
        revShare3: publisher?.revShare3?.percentage,
        currency: publisher?.stripe?.currency,
        lowPrice3: publisher?.revShare3?.minPrice,
        highPrice3: publisher?.revShare3?.maxPrice,
        productRevShare: publisher?.productRevShare1?.percentage,
        productRevShare2: publisher?.productRevShare2?.percentage,
        productLowPrice2: publisher?.productRevShare2?.minPrice,
        productHighPrice2: publisher?.productRevShare2?.maxPrice,
        productRevShare3: publisher?.productRevShare3?.percentage,
        productLowPrice3: publisher?.productRevShare3?.minPrice,
        productHighPrice3: publisher?.productRevShare3?.maxPrice,
        paywallPadding: publisher?.style?.paywallPadding,
        paywallButtonClass: publisher?.style?.paywallButtonClass,
        paywallButtonColor: publisher?.style?.paywallButtonColor,
        paywallButtonTextColor: publisher?.style?.paywallButtonTextColor,
        paywallButtonTextSize: publisher?.style?.paywallButtonTextSize,
        paywallContainerClass: publisher?.style?.paywallContainerClass,
        paddingTop: publisher?.style?.paddingTop,
        paddingBottom: publisher?.style?.paddingBottom,
        paywallPromptTextClass: publisher?.style?.paywallPromptTextClass,
        paywallPromptTextColor: publisher?.style?.paywallPromptTextColor,
        paywallPromptTextSize: publisher?.style?.paywallPromptTextSize,
        paywallButtonTag: publisher?.style?.paywallButtonTag,
        // Ghost defaults to the alternative button position (next to Subscribe)
        isButtonNext:
          publisher?.style?.isButtonNext ??
          ((publisher?.activeCMS ?? CMS.Ghost) === CMS.Ghost),
        paywallButtonStyles: publisher?.style?.paywallButtonStyles,
        paywallPromptTextStyles: publisher?.style?.paywallPromptTextStyles,
        paywallButtonWrapperClass: publisher?.style?.paywallButtonWrapperClass,
        ghContentClass: publisher?.style?.ghContentClass,
        iframeMaxWidth: publisher?.style?.iframeMaxWidth,
        paymentButtonColor: publisher?.primaryColor,
        textColor: publisher?.secondaryColor,
        viewEvent: publisher?.viewEventActive ? "Yes" : "No",
        subscribeOverride: !!publisher?.subscribeOverride,
        actaPromptText: publisher?.actaPromptText,
        actaButtonText:
          publisher?.actaButtonText || TEXTS.DEFAULT_ACTA_BUTTON_TEXT,
        apiKey: publisher?.ghostAPI?.apiKey,
        apiURL: publisher?.ghostAPI?.apiURL,
        ghostMemberOptIn: !!publisher?.ghostMemberOptIn,
        ghostEmailPrefill: !!publisher?.ghostEmailPrefill,
        activeCMS: publisher.activeCMS ?? CMS.Ghost,
        beehiivApiKey: publisher?.beehiivAPI?.apiKey,
        beehiivPublicationId: publisher?.beehiivAPI?.publicationId,
        wordpressPluginEndpoint: publisher?.wordpressAPI?.pluginEndpoint,
        wordpressPluginSecretKey: publisher?.wordpressAPI?.pluginSecretKey,
      });
      setActiveCMS(publisher?.activeCMS ?? CMS.Ghost);
    }
  }, [publisher, dispatch, form]);

  const currencies = useMemo(() => {
    const displayNames = new Intl.DisplayNames(["en"], { type: "currency" });
    const getName = (code) => {
      try { return displayNames.of(code.toUpperCase()) || code.toUpperCase(); }
      catch { return code.toUpperCase(); }
    };
    const pinnedCodes = ["usd", "eur", "gbp"];
    const pinnedSet = new Set(pinnedCodes);
    const pinned = pinnedCodes.map((code) => ({
      label: `${code.toUpperCase()} — ${getName(code)}`,
      value: code.toLowerCase(),
    }));
    const rest = supportedCurrencies
      .filter((c) => !pinnedSet.has(c.toLowerCase()))
      .map((code) => ({
        label: `${code.toUpperCase()} — ${getName(code)}`,
        value: code.toLowerCase(),
      }));
    return [
      { label: "Popular", options: pinned },
      { label: "All currencies", options: rest },
    ];
  }, [supportedCurrencies]);

  const handleSyncBeehiiv = () => {
    Modal.confirm({
      title: "Sync Beehiiv Posts",
      content: "This will trigger a sync job to fetch all posts from Beehiiv. Continue?",
      onOk: async () => {
        await dispatch(syncBeehiiv({ publisherId }));
        // Refresh publisher data to show updated status
        dispatch(getPublisher({ publisherId }));
      },
    });
  };

  const handleRunDiagnostics = () => {
    Modal.confirm({
      title: "Run Beehiiv Diagnostics",
      content: "This will run diagnostics on the Beehiiv integration. Continue?",
      onOk: async () => {
        await dispatch(runBeehiivDiagnostics({ publisherId }));
      },
    });
  };

  const onFinish = (values) => {
    const paymentButtonColor =
      values?.paymentButtonColor?.metaColor?.originalInput;
    const paymentTextColor = values?.textColor?.metaColor?.originalInput;
    const actaPromptText =
      values?.actaPromptText === undefined ||
        values?.actaPromptText === TEXTS.EMPTY_TEXT
        ? TEXTS.DEFAULT_ACTA_PROMPT_TEXT
        : values?.actaPromptText;
    const actaButtonText =
      values?.actaButtonText === undefined ||
        values?.actaButtonText === TEXTS.EMPTY_TEXT
        ? TEXTS.DEFAULT_ACTA_BUTTON_TEXT
        : values?.actaButtonText;

    const publisherData = {
      name: values?.publisherName,
      email: values?.email,
      website: values?.website,
      userFirstName: values?.firstName,
      userLastName: values?.lastName,
      country: values?.country,
      articlePrice: values?.articlePrice,
      viewEventActive: values?.viewEvent === "Yes" ? true : false,
      subscribeOverride: !!values?.subscribeOverride,
      revShare1: {
        percentage: values?.revShare,
        minPrice: values?.miniPrice,
      },

      revShare2: {
        percentage: values?.revShare2 ?? null,
        minPrice: values?.lowPrice2 ?? null,
        maxPrice: values?.highPrice2 ?? null,
      },
      revShare3: {
        percentage: values?.revShare3 ?? null,
        minPrice: values?.lowPrice3 ?? null,
        maxPrice: values?.highPrice3 ?? null,
      },
      productRevShare1: {
        percentage: values?.productRevShare ?? null,
      },
      productRevShare2: {
        percentage: values?.productRevShare2 ?? null,
        minPrice: values?.productLowPrice2 ?? null,
        maxPrice: values?.productHighPrice2 ?? null,
      },
      productRevShare3: {
        percentage: values?.productRevShare3 ?? null,
        minPrice: values?.productLowPrice3 ?? null,
        maxPrice: values?.productHighPrice3 ?? null,
      },
      stripe: {
        currency: values?.currency,
      },
      // abTest: values?.abTest === "Yes" ? true : false,
      active: values?.active === "Yes" ? true : false,

      checkoutemail: values?.checkoutemail === "Yes" ? true : false,
      activeCMS: values.activeCMS,
      beehiivAPI: {
        apiKey: values.beehiivApiKey,
        publicationId: values.beehiivPublicationId,
      },
      ghostAPI: {
        apiKey: values?.apiKey,
        apiURL: values?.apiURL,
      },
      ghostMemberOptIn: !!values?.ghostMemberOptIn,
      ghostEmailPrefill: !!values?.ghostEmailPrefill,
      wordpressAPI: {
        siteUrl: values?.website || null,
        username: null,
        applicationPassword: null,
        // pluginEndpoint and pluginSecretKey are set by the WordPress plugin auto-connection,
        // not by the admin form. We preserve existing values by not including them here.
      },
      style: {
        paywallButtonClass: values?.paywallButtonClass || null,
        isButtonNext: values?.isButtonNext,
        paywallButtonColor:
          convertColorObjectToHex(
            values?.paywallButtonColor,
            publisher?.style?.paywallButtonColor
          ) ?? null,
        paywallButtonTextColor:
          convertColorObjectToHex(
            values?.paywallButtonTextColor,
            publisher?.style?.paywallButtonTextColor
          ) ?? null,
        paywallButtonTextSize: values?.paywallButtonTextSize ?? null,
        paywallContainerClass: values?.paywallContainerClass,
        paywallPadding: values?.paywallPadding,
        paywallPromptTextClass: values?.paywallPromptTextClass || null,
        paywallPromptTextColor:
          convertColorObjectToHex(
            values?.paywallPromptTextColor,
            publisher?.style?.paywallPromptTextColor
          ) ?? null,
        paywallPromptTextSize: values?.paywallPromptTextSize ?? null,
        paddingTop: values?.paddingTop ?? null,
        paddingBottom: values?.paddingBottom ?? null,
        paywallButtonTag: values?.paywallButtonTag,
        paywallButtonWrapperClass: values?.paywallButtonWrapperClass || null,
        ghContentClass: values?.ghContentClass || null,
        paywallButtonStyles: values?.paywallButtonStyles || null,
        paywallPromptTextStyles: values?.paywallPromptTextStyles || null,
        iframeMaxWidth: values?.iframeMaxWidth || null,
      },

      primaryColor: paymentButtonColor
        ? typeof paymentButtonColor === "object" &&
          Object.keys(paymentButtonColor)?.length > 0
          ? hsvToHexWithAlpha(
            paymentButtonColor?.h,
            paymentButtonColor?.s,
            paymentButtonColor?.v,
            paymentButtonColor?.a
          )
          : "#" + paymentButtonColor
        : publisher?.primaryColor,
      secondaryColor: paymentTextColor
        ? typeof paymentTextColor === "object" &&
          Object.keys(paymentTextColor)?.length > 0
          ? hsvToHexWithAlpha(
            paymentTextColor?.h,
            paymentTextColor?.s,
            paymentTextColor?.v,
            paymentTextColor?.a
          )
          : "#" + paymentTextColor
        : publisher?.secondaryColor,
      actaPromptText: actaPromptText,
      actaButtonText: actaButtonText,
    };
    dispatch(editPublisher({ publisherId, publisherData, navigate }));
  };

  // Filter `option.label` match the user type `input`
  const filterOption = (input, option) =>
    (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const countries = useMemo(() => {
    const countries = lookup.countries;
    const countryOptions = [];
    countries.forEach((country) => {
      countryOptions.push({
        label: country.country,
        value: country.iso2,
      });
    });
    return countryOptions;
  }, []);

  const paywallButtonTags = [
    {
      label: "a",
      value: "a",
    },
    {
      label: "button",
      value: "button",
    },
    {
      label: "div",
      value: "div",
    },
  ];



  const renderStyleConfig = () => {
    if (activeCMS === CMS.Beehiiv) {
      return renderBeehiivStyleConfig()
    }
    if (activeCMS === CMS.WordPress) {
      return renderWordPressStyleConfig()
    }

    return renderGhostStyleConfig()
  }

  const renderGhostStyleConfig = () => {
    return (
      <>
        <Row gutter={28}>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallContainerClass"
              label="Paywall container class"
              rules={[{ required: true, message: "required" }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallButtonClass"
              label="Paywall button class"
              rules={[{ required: true, message: "required" }]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallPromptTextClass"
              label="Paywall prompt text class"
            >
              <Input />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallPromptTextSize"
              label="Paywall prompt text size"
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={textSizeTags}
                allowClear
              />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={28} style={{ marginTop: 10 }}>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallButtonWrapperClass"
              label="Paywall button wrapper class"
            >
              <Input />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallButtonTag"
              label="Paywall button tag"
              rules={[{ required: true, message: "required" }]}
            >
              <Select
                showSearch
                optionFilterProp="label"
                options={paywallButtonTags}
              />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallButtonTextSize"
              label="Paywall button text size"
            >
              <InputNumber min={1} suffix={<p>px</p>} />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item name="paywallPadding" label="Paywall Padding">
              <InputNumber min={1} suffix={<p>px</p>} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={28} style={{ marginTop: 10 }}>
          <Col lg={{ span: 6 }}>
            <Form.Item name="ghContentClass" label="Gh content class">
              <Input />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item name="actaPromptText" label="Acta prompt text">
              <Input />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item name="actaButtonText" label="Acta button text">
              <Input />
            </Form.Item>
          </Col>
          <Col lg={{ span: 4 }}>
            <Form.Item
              name="isButtonNext"
              valuePropName="checked"
              label="Button alternative position"
            >
              <Checkbox />
            </Form.Item>
          </Col>
          <Col lg={{ span: 4 }}>
            <Form.Item name="iframeMaxWidth" label="Iframe max width (px)">
              <Input placeholder="e.g. 700" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={28} style={{ marginTop: 10 }}>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallButtonTextColor"
              label="Paywall button text color"
            >
              <ColorPicker allowClear showText defaultValue={null} />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item name="paywallButtonColor" label="Paywall button color">
              <ColorPicker allowClear showText defaultValue={null} />
            </Form.Item>
          </Col>

          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallPromptTextColor"
              label="Paywall prompt text color"
            >
              <ColorPicker allowClear defaultValue={null} showText />
            </Form.Item>
          </Col>
          {/* Payment iframe button colors — commented out to use defaults; re-enable if custom colors needed
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paymentButtonColor"
              initialValue="#4209EE"
              label="Payment button color"
            >
              <ColorPicker defaultValue="#4209EE" showText />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="textColor"
              initialValue="#FFFFFF"
              label="Outline / text color"
            >
              <ColorPicker showText />
            </Form.Item>
          </Col>
          */}
        </Row>
      </>
    )
  }


  const renderBeehiivStyleConfig = () => {
    return (
      <>
        <Row gutter={28}>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallButtonClass"
              label="Paywall Button Classes"
            >
              <Input />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallButtonStyles"
              label="Paywall button styles"
            >
              <Input />
            </Form.Item>
          </Col>
          <Col lg={{ span: 4 }}>
            <Form.Item name="iframeMaxWidth" label="Iframe max width (px)">
              <Input placeholder="e.g. 700" />
            </Form.Item>
          </Col>
        </Row>
      </>
    )
  }

  const renderWordPressStyleConfig = () => {
    return (
      <>
        <Row gutter={28}>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallContainerClass"
              label="Paywall container class"
              rules={[{ required: true, message: "required" }]}
            >
              <Input placeholder="e.g. .memberpress-paywall" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={28} style={{ marginTop: 10 }}>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallPromptTextClass"
              label="Paywall prompt text class"
            >
              <Input placeholder="e.g. wp-block-heading" />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallPromptTextStyles"
              label="Prompt style"
            >
              <Input placeholder="e.g. font-size:1.2rem;font-weight:600" />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item name="actaPromptText" label="Prompt text">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={28} style={{ marginTop: 10 }}>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallButtonClass"
              label="Paywall button class"
            >
              <Input placeholder="e.g. wp-block-button__link" />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallButtonStyles"
              label="Button style"
            >
              <Input placeholder="e.g. background:#000;border-radius:4px" />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item name="actaButtonText" label="Button text">
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={28} style={{ marginTop: 10 }}>
          {/* Payment iframe button colors — commented out to use defaults; re-enable if custom colors needed
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="textColor"
              initialValue="#FFFFFF"
              label="Outline / text color"
            >
              <ColorPicker showText />
            </Form.Item>
          </Col>
          */}
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallButtonColor"
              label="Button color (override)"
            >
              <ColorPicker showText />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item
              name="paywallButtonTextColor"
              label="Button text color (override)"
            >
              <ColorPicker showText />
            </Form.Item>
          </Col>
          <Col lg={{ span: 6 }}>
            <Form.Item name="isButtonNext" valuePropName="checked" label="Button placed next to subscribe">
              <Checkbox />
            </Form.Item>
          </Col>
          <Col lg={{ span: 8 }}>
            <Form.Item
              name="subscribeOverride"
              valuePropName="checked"
              label="Pay-per-article only (replace subscribe gate)"
            >
              <Checkbox />
            </Form.Item>
          </Col>
          <Col lg={{ span: 4 }}>
            <Form.Item name="iframeMaxWidth" label="Iframe max width (px)">
              <Input placeholder="e.g. 700" />
            </Form.Item>
          </Col>
        </Row>
      </>
    )
  }

  const renderBeehiivConfig = () => {
    const getStatusType = (status) => {
      switch (status) {
        case 'Pending':
          return '#eca52b';
        case 'InProgress':
          return '#eca52b';
        case 'Completed':
          return '#28a745';
        case 'Failed':
          return 'red';
        default:
          return 'gray';
      }
    };

    const getStatusLabel = (status) => {
      switch (status) {
        case 'Pending':
          return 'Pending';
        case 'InProgress':
          return 'In Progress';
        case 'Completed':
          return 'Completed';
        case 'Failed':
          return 'Failed';
        default:
          return 'Unknown';
      }
    };

    return (
      <>
        {publisher.beehiivAPI?.initiateStatus && (<Row>
          <Col lg={{ span: 6 }}>
            <Form.Item label="Data Initiated Status"
              style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
            >
              <Row style={{alignItems: 'center', gap: 10}}>
                <Button
                  style={{
                    backgroundColor: getStatusType(publisher?.beehiivAPI?.initiateStatus),
                    color: 'white',
                    fontWeight: 'bold'
                  }}
                >
                  {getStatusLabel(publisher?.beehiivAPI?.initiateStatus)}
                </Button>
                <Button
                  type="primary"
                  onClick={handleSyncBeehiiv}
                  style={{ marginLeft: '8px' }}
                >
                  Sync Posts
                </Button>
                <Button
                  type="primary"
                  onClick={handleRunDiagnostics}
                  style={{ marginLeft: '8px' }}
                >
                  Diagnostics
                </Button>
                {
                  publisher?.beehiivAPI?.initiateStatus === 'Failed' &&
                  (
                    <p style={{ color: 'red' }}>{publisher?.beehiivAPI?.reason}</p>
                  )
                }
              </Row>
            </Form.Item>
          </Col>
        </Row>)}
        {diagnosticsResults && (
          <Row style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Card
                title="Beehiiv Diagnostics Results"
                extra={
                  <Button type="link" onClick={() => dispatch(clearDiagnostics())} style={{ padding: 0 }}>
                    Close
                  </Button>
                }
              >
                <pre style={{
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                  backgroundColor: "#f5f5f5",
                  padding: 15,
                  borderRadius: 4,
                  maxHeight: 400,
                  overflow: "auto",
                  margin: 0,
                }}>
                  {typeof diagnosticsResults === "object"
                    ? JSON.stringify(diagnosticsResults, null, 2)
                    : String(diagnosticsResults)}
                </pre>
              </Card>
            </Col>
          </Row>
        )}
        <Row gutter={26}>
          <Col lg={{ span: 12 }}>
            <Form.Item
              name="beehiivApiKey"
              label="API Key"
              rules={[{ required: true, message: "required" }]}
            >
              <Input />
            </Form.Item>
          </Col>

          <Col lg={{ span: 9 }}>
            <Form.Item
              name="beehiivPublicationId"
              label="Publication ID"
              rules={[
                { required: true, message: "required" },
                {
                  type: "string",
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </>
    )
  }

  const renderGhostConfig = () => {
    return (
      < Row gutter={26}>
        <Col span={24}>
          <Row gutter={26}>
            <Col lg={{ span: 10 }}>
              <Form.Item
                name="ghostMemberOptIn"
                valuePropName="checked"
                label={'Show "Join as a free member" checkbox'}
                tooltip="Adds the buyer to this publisher's Ghost members and subscribes them to the newsletter."
              >
                <Checkbox />
              </Form.Item>
            </Col>
            <Col lg={{ span: 10 }}>
              <Form.Item
                name="ghostEmailPrefill"
                valuePropName="checked"
                label="Prefill email for logged-in Ghost members"
                tooltip="Looks up the logged-in Ghost member on the publisher's page so the paywall can skip the email step."
              >
                <Checkbox />
              </Form.Item>
            </Col>
          </Row>
        </Col>
        <Col lg={{ span: 12 }}>
          <Form.Item
            name="apiKey"
            label="API Key"
            rules={[{ required: true, message: "required" }]}
          >
            <Input />
          </Form.Item>
        </Col>

        <Col lg={{ span: 9 }}>
          <Form.Item
            name="apiURL"
            label="API URL"
            rules={[
              { required: true, message: "required" },
              {
                type: "url",
                message: "Please enter a valid URL",
              },
              {
                type: "string",
              },
            ]}
          >
            <Input />
          </Form.Item>
        </Col>
      </Row >
    )
  }

  const renderWordPressConfig = () => {
    const isPluginConnected = !!publisher?.wordpressAPI?.pluginEndpoint;

    return (
      <>
        {/* Publisher ID — copy to share with publisher */}
        <Row gutter={26} style={{ marginBottom: 16 }}>
          <Col span={24}>
            <div style={{
              display: "flex",
              alignItems: "center",
              gap: 12,
              padding: "12px 16px",
              background: "#f5f5f5",
              borderRadius: 6,
            }}>
              <strong>Publisher ID:</strong>
              <code style={{ fontSize: 16, letterSpacing: 1 }}>{publisherId}</code>
              <Button
                type="text"
                icon={<FiCopy />}
                onClick={() => copyToClipboard(publisherId, messageApi)}
                size="small"
              >
                Copy
              </Button>
            </div>
            <p style={{ color: "#888", marginTop: 4, fontSize: 12 }}>
              The publisher enters this ID in WordPress → Settings → Acta → "Connect to Acta"
            </p>
          </Col>
        </Row>

        {/* Connection status */}
        {isPluginConnected ? (
          <Row gutter={26}>
            <Col span={24}>
              <div style={{
                padding: "12px 16px",
                background: "#f6ffed",
                border: "1px solid #b7eb8f",
                borderRadius: 6,
                marginBottom: 16,
              }}>
                <Tag color="success" style={{ marginBottom: 8 }}>Plugin Connected</Tag>
                <div style={{ fontSize: 13, color: "#555" }}>
                  <strong>Endpoint:</strong>{" "}
                  <code>{publisher?.wordpressAPI?.pluginEndpoint}</code>
                </div>
              </div>
            </Col>
          </Row>
        ) : (
          <Row gutter={26}>
            <Col span={24}>
              <div style={{
                padding: "12px 16px",
                background: "#fffbe6",
                border: "1px solid #ffe58f",
                borderRadius: 6,
                marginBottom: 16,
              }}>
                <Tag color="warning">Plugin Not Connected</Tag>
                <p style={{ margin: "8px 0 0", fontSize: 13, color: "#555" }}>
                  Ask the publisher to install the Acta plugin on their WordPress site, then enter the Publisher ID above in WordPress → Settings → Acta.
                </p>
              </div>
            </Col>
          </Row>
        )}
      </>
    )
  }


  const renderCmsConfig = () => {
    return (
      <>
        <Row gutter={26}>
          <Form.Item name="activeCMS" label="CMS Type">
            <Radio.Group onChange={handleCmsChange} value={activeCMS}>
              <Radio value={CMS.Ghost}>{CMS.Ghost}</Radio>
              <Radio value={CMS.Beehiiv}>{CMS.Beehiiv}</Radio>
              <Radio value={CMS.WordPress}>{CMS.WordPress}</Radio>
              <Radio value={CMS.Direct}>{CMS.Direct}</Radio>
            </Radio.Group>
          </Form.Item>
        </Row>
        {activeCMS === CMS.Beehiiv && renderBeehiivConfig()}
        {activeCMS === CMS.Ghost && renderGhostConfig()}
        {activeCMS === CMS.WordPress && renderWordPressConfig()}
      </>
    )
  }


  return (
    <Wrapper>
      {contextHolder}
      <CustomContainer>
        <Form form={form} onFinish={onFinish} layout="vertical">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24 }}>
            <TopHeading style={{ margin: 0 }}>Edit Publishers</TopHeading>
            <SaveBtn htmlType="button">
              {loading ? "loading..." : "Save"}
            </SaveBtn>
          </div>
          {/* Publisher Details */}
          <Card title="Publisher Details" style={{ marginBottom: 24 }}>
            <Row gutter={[16, 0]}>
              <Col lg={{ span: 7 }}>
                <Form.Item
                  name="publisherName"
                  label="Publisher Name"
                  rules={[{ required: true, message: "required" }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col lg={{ span: 5 }}>
                <Form.Item
                  name="firstName"
                  label="First Name"
                  rules={[{ required: true, message: "required" }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col lg={{ span: 5 }}>
                <Form.Item
                  name="lastName"
                  label="Last Name"
                  rules={[{ required: true, message: "required" }]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col lg={{ span: 7 }}>
                <Form.Item
                  name="email"
                  label="Email"
                  rules={[
                    { type: "email", message: "email is not valid" },
                    { required: true, message: "required" },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 0]}>
              <Col lg={{ span: 14 }}>
                <Form.Item name="website" label="Website">
                  <Input />
                </Form.Item>
              </Col>
              <Col lg={{ span: 10 }}>
                <Form.Item label="Portal access">
                  <Tag color={publisher?.portalClaimed ? "green" : "default"}>
                    {publisher?.portalClaimed ? "Claimed" : "Not claimed"}
                  </Tag>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Location & Status */}
          <Card title="Location & Status" style={{ marginBottom: 24 }}>
            <Row gutter={[16, 0]}>
              <Col lg={{ span: 8 }}>
                <Form.Item
                  name="country"
                  label="Country"
                  rules={[{ required: true, message: "required" }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="children"
                    filterOption={filterOption}
                    options={countries}
                  />
                </Form.Item>
              </Col>
              <Col lg={{ span: 6 }}>
                <Form.Item
                  name="currency"
                  label="Currency"
                  rules={[{ required: true, message: "required" }]}
                >
                  <Select
                    showSearch
                    optionFilterProp="label"
                    loading={isSupportedCurrenciesLoading}
                    options={currencies}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 0]}>
              <Col lg={{ span: 4 }}>
                <Form.Item
                  name="active"
                  label="Active?"
                  rules={[{ required: true, message: "required" }]}
                >
                  <Radio.Group>
                    <Radio value="Yes">Yes</Radio>
                    <Radio value="No">No</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col lg={{ span: 6 }}>
                <Form.Item
                  name="viewEvent"
                  label="Page View Event Active?"
                  rules={[{ required: true, message: "required" }]}
                >
                  <Radio.Group>
                    <Radio value="Yes">Yes</Radio>
                    <Radio value="No">No</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
              <Col lg={{ span: 4 }}>
                <Form.Item
                  name="checkoutemail"
                  label="Checkout Email?"
                  rules={[{ required: true, message: "required" }]}
                >
                  <Radio.Group>
                    <Radio value="Yes">Yes</Radio>
                    <Radio value="No">No</Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Article Pricing & Revenue Share */}
          <Card title="Article Pricing & Revenue Share" style={{ marginBottom: 24 }}>
            <Row gutter={[16, 0]}>
              <Col lg={{ span: 5 }}>
                <Form.Item
                  name="articlePrice"
                  label="Default Article price"
                  rules={[{ required: true, message: "required" }]}
                >
                  <InputNumber prefix={<TbCurrencyDollar />} />
                </Form.Item>
              </Col>
              <Col lg={{ span: 4 }}>
                <Form.Item
                  name="revShare"
                  label="Rev share"
                  extra="Defaults to 15% if left blank"
                >
                  <InputNumber prefix={<AiOutlinePercentage />} />
                </Form.Item>
              </Col>
              <Col lg={{ span: 5 }}>
                <Form.Item
                  name="miniPrice"
                  label="Minimum price"
                  rules={[{ required: true, message: "required" }]}
                >
                  <InputNumber prefix={<TbCurrencyDollar />} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 0]}>
              <Col lg={{ span: 4 }}>
                <Form.Item name="revShare2" label="Rev Share 2">
                  <InputNumber prefix={<AiOutlinePercentage />} />
                </Form.Item>
              </Col>
              <Col lg={{ span: 4 }}>
                <Form.Item name="lowPrice2" label="Low Price 2">
                  <InputNumber prefix={<TbCurrencyDollar />} />
                </Form.Item>
              </Col>
              <Col lg={{ span: 4 }}>
                <Form.Item name="highPrice2" label="High Price 2">
                  <InputNumber prefix={<TbCurrencyDollar />} />
                </Form.Item>
              </Col>
              <Col lg={{ span: 4 }}>
                <Form.Item name="revShare3" label="Rev Share 3">
                  <InputNumber prefix={<AiOutlinePercentage />} />
                </Form.Item>
              </Col>
              <Col lg={{ span: 4 }}>
                <Form.Item name="lowPrice3" label="Low Price 3">
                  <InputNumber prefix={<TbCurrencyDollar />} />
                </Form.Item>
              </Col>
              <Col lg={{ span: 4 }}>
                <Form.Item name="highPrice3" label="High Price 3">
                  <InputNumber prefix={<TbCurrencyDollar />} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* Product Pricing & Revenue Share */}
          <Card title="Product Pricing & Revenue Share" style={{ marginBottom: 24 }}>
            <Row gutter={[16, 0]}>
              <Col lg={{ span: 4 }}>
                <Form.Item
                  name="productRevShare"
                  label="Rev share"
                  extra="Defaults to 4% if left blank"
                >
                  <InputNumber prefix={<AiOutlinePercentage />} />
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={[16, 0]}>
              <Col lg={{ span: 4 }}>
                <Form.Item name="productRevShare2" label="Rev Share 2">
                  <InputNumber prefix={<AiOutlinePercentage />} />
                </Form.Item>
              </Col>
              <Col lg={{ span: 4 }}>
                <Form.Item name="productLowPrice2" label="Low Price 2">
                  <InputNumber prefix={<TbCurrencyDollar />} />
                </Form.Item>
              </Col>
              <Col lg={{ span: 4 }}>
                <Form.Item name="productHighPrice2" label="High Price 2">
                  <InputNumber prefix={<TbCurrencyDollar />} />
                </Form.Item>
              </Col>
              <Col lg={{ span: 4 }}>
                <Form.Item name="productRevShare3" label="Rev Share 3">
                  <InputNumber prefix={<AiOutlinePercentage />} />
                </Form.Item>
              </Col>
              <Col lg={{ span: 4 }}>
                <Form.Item name="productLowPrice3" label="Low Price 3">
                  <InputNumber prefix={<TbCurrencyDollar />} />
                </Form.Item>
              </Col>
              <Col lg={{ span: 4 }}>
                <Form.Item name="productHighPrice3" label="High Price 3">
                  <InputNumber prefix={<TbCurrencyDollar />} />
                </Form.Item>
              </Col>
            </Row>
          </Card>

          {/* CMS Configuration */}
          <Card title="CMS Configuration" style={{ marginBottom: 24 }}>
            {renderCmsConfig()}
          </Card>

          {/* Styles — not needed for Direct CMS */}
          {activeCMS !== CMS.Direct && (
            <Card title="Styles">
              {renderStyleConfig()}
            </Card>
          )}
        </Form>
      </CustomContainer>
    </Wrapper>
  );
};

export default EditPublishers;
